import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Params } from '@angular/router';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, first, map, startWith, takeUntil } from 'rxjs/operators';
import { Brand, BrandRequest, Company, DeleteBrandRequest, FileVaultCategory, FileVaultReference, FileVaultReferenceRelation, FileVaultSubjectType, GetFileVaultReferenceRelationsRequest, ListBrandsRequest, ListBrandsResponse, QualityLevel, SituViewWebApiClient, UpdateBrandRequest } from 'situview-webapi-client';
import { ConfirmDialogComponent } from 'src/app/components';
import { ConfirmDialogData } from 'src/app/components/confirm-dialog/ConfirmDialogData';
import { AuthService, StateService } from 'src/app/services';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { CreateBrandDialogComponent, CreateBrandDialogData } from '../create-brand-dialog/create-brand-dialog/create-brand-dialog.component';
import { FileVaultService } from 'src/app/services/file-vault.service';


interface BrandRow {
  brand: Brand;
  logoId: string;
  form: FormGroup | undefined;
  editMode: boolean;
}

@Component({
  selector: 'app-brands',
  templateUrl: './brands.component.html',
  styleUrls: ['./brands.component.scss']
})
export class BrandsComponent extends SituBaseComponent implements OnDestroy {

  public loading: number = 0;
  public allBrands: BrandRow[] = [];
  public allCompanies: Company[] = [];
  public readonly QualityLevel = QualityLevel;

  public nameFilterControl = new FormControl();
  public filteredBrands: Observable<BrandRow[]>;
  public readonly destroyed = new Subject<null>();

  constructor(
    readonly authService: AuthService,
    readonly stateService: StateService,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly situViewClient: SituViewWebApiClient,
    private readonly activeRoute: ActivatedRoute,
    private readonly fileVaultService: FileVaultService,
  ) {
    super(authService, stateService);

    this.activeRoute.queryParams.pipe(
      takeUntil(this.destroyed))
      .subscribe(
        (params: Params) => {
          if (params?.filter) {
            this.nameFilterControl.setValue(params.filter);
          }
        }
      );

    this.filteredBrands = combineLatest([
      this.nameFilterControl.valueChanges.pipe(
        startWith(this.nameFilterControl.value)),
    ]).pipe(
      map(([brandName]) => {
        let brands = this.allBrands;
        if (brandName) {
          brands = brands.filter(b => b.brand.name?.toLowerCase().includes(brandName.toLowerCase()));
        }
        return brands;
      }),
    );
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.destroyed.next();
    this.destroyed.complete();
  }

  public onOpenAddBrands(names?: string[]): void {
    this.dialog.open<CreateBrandDialogComponent, CreateBrandDialogData, boolean>(CreateBrandDialogComponent, {
      data: {
        brands: names,
        companyId: this.selectedCompany?.id
      },
      width: '80vw',
      minWidth: '350px',
      maxWidth: '1400px',
    }).afterClosed().pipe(
      first()
    ).subscribe((newBrands?: boolean) => {
      if (newBrands) {
        this.loadBrands();
      }
    });
  }

  public onEditBrand(brandRow: BrandRow): void {

    if (!brandRow.form) {
      brandRow.form = new FormGroup({
        name: new FormControl(brandRow.brand.name || ''),
        companyId: new FormControl({ value: brandRow.brand.companyId || '', disabled: true }, [Validators.required]),
        qualityLevel: new FormControl(brandRow.brand.qualityLevel),
        logoFileVaultId: new FormControl(),
        showBrandLogoOnly: new FormControl(brandRow.brand.showBrandLogoOnly),
      });
    }

    brandRow.editMode = true;
  }

  public onDeleteBrand(brandRow: BrandRow): void {
    this.dialog.open<ConfirmDialogComponent, ConfirmDialogData, boolean>(ConfirmDialogComponent, {
      data: {
        confirmText: "Delete Brand",
        title: "Delete Brand?",
        actionIcon: 'delete',
        cancelText: 'cancel',
        message: 'Deleting brands is permanent. This will remove the brand from all associated products',
      } as ConfirmDialogData
    }).afterClosed().pipe(
      first(),
      filter(confirm => confirm ?? false)
    ).subscribe(() => {
      this.situViewClient.deleteBrand(new DeleteBrandRequest({ companyId: brandRow.brand.companyId, brandId: brandRow.brand.id }))
        .then((response) => {
          if (!response.success) {
            this.snackBar.open(`Failed to delete brand ${brandRow.brand.name}`, undefined, { duration: 3000 });
          } else {
            this.allBrands.splice(this.allBrands.indexOf(brandRow), 1);
            this.snackBar.open(`Deleted brand ${brandRow.brand.name}`, undefined, { duration: 3000 });
          }
        })
        .catch((reason) => this.snackBar.open(`Failed to update brand ${brandRow.brand.name}`, undefined, { duration: 3000 }));
    });
  }

  public onSaveChanges(brandRow: BrandRow): void {

    this.situViewClient.updateBrand(new UpdateBrandRequest({
      brand: new BrandRequest({
        ...brandRow.brand,
        ...brandRow.form?.getRawValue(),
        logoFileVaultId: brandRow.logoId
      })
    })
    ).then((response) => {
      if (!response.success) {
        this.snackBar.open(`Failed to update brand ${brandRow.brand.name}`, undefined, { duration: 3000 });
      } else {
        brandRow.brand = response.brand!;
        brandRow.editMode = false;
        this.snackBar.open(`Updated brand ${brandRow.brand.name}`, undefined, { duration: 3000 });
      }
    })
      .catch((reason) => this.snackBar.open(`Failed to update brand ${brandRow.brand.name}`, undefined, { duration: 3000 }));
  }

  protected onSelectedCompanyChange(): void {
    this.loadBrands();
  }

  private loadBrands(): Promise<BrandRow[] | undefined> {
    this.loading++;
    return this.situViewClient.listBrands(new ListBrandsRequest({ companyId: this.selectedCompany?.id }))
      .then((res: ListBrandsResponse) => {

        let brands = res.brands?.map(b => ({
          brand: b,
          form: undefined,
          editMode: false,
        } as BrandRow));

        this.allBrands = brands || [];
        this.allBrands.sort((a, b) => a.brand.name!.localeCompare(b.brand.name!));
        this.nameFilterControl.updateValueAndValidity();
        return brands;
      })
      .catch((r) => {
        this.snackBar.open("Failed to load brands, please try again", undefined, { duration: 3000 });
        return undefined;
      })
      .finally(() => {
        this.loading--;
      });
  }
}